import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, X as XIcon, Sparkles, ChevronDown, Star, Phone, ShoppingCart, Calculator, PencilRuler } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import CostCalculator from "@/pages/CostCalculator";
import SEO from "@/components/SEO";

const DESIGN_TIERS = [
  {
    id: "basic",
    nameKey: "pricingPage.tierBasic",
    focusKey: "pricingPage.focusNaksha",
    mrp: 5330,
    discount: 70,
    price: 1599,
    validityKey: "pricingPage.validity30",
  },
  {
    id: "standard",
    nameKey: "pricingPage.tierStandard",
    focusKey: "pricingPage.focusArchitectural",
    mrp: 13330,
    discount: 70,
    price: 3999,
    validityKey: "pricingPage.validity90",
  },
  {
    id: "popular",
    nameKey: "pricingPage.tierPopular",
    focusKey: "pricingPage.focusConstruction",
    mrp: 23330,
    discount: 70,
    price: 6999,
    validityKey: "pricingPage.validity365",
    highlight: true,
  },
  {
    id: "premium",
    nameKey: "pricingPage.tierPremium",
    focusKey: "pricingPage.focusStructural",
    mrp: 33330,
    discount: 70,
    price: 9999,
    validityKey: "pricingPage.validity365",
  },
];

// true = included, false = not included, "validity" = looks up tier's validity label
const FEATURE_ROWS = [
  { id: "2d",       labelKey: "pricingPage.f2d",       values: [true,  true,  true,  true] },
  { id: "elev",     labelKey: "pricingPage.fElev",     values: [false, true,  true,  true] },
  { id: "3d",       labelKey: "pricingPage.f3d",       values: [false, false, true,  true] },
  { id: "plumb",    labelKey: "pricingPage.fPlumb",    values: [false, false, true,  true] },
  { id: "elec",     labelKey: "pricingPage.fElec",     values: [false, false, true,  true] },
  { id: "struct",   labelKey: "pricingPage.fStruct",   values: [false, false, false, true] },
  { id: "support",  labelKey: "pricingPage.fSupport",  values: [false, false, false, true] },
  { id: "validity", labelKey: "pricingPage.fValidity", values: ["validity","validity","validity","validity"] },
];

const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const ValueCell = ({ v, highlight, t, tierIdx }) => {
  if (typeof v === "boolean") {
    return v ? (
      <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${highlight ? "text-emerald-700" : "text-emerald-600"}`}>
        <CheckCircle2 className="w-4 h-4" /> {t("pricingPage.included")}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-sm text-slate-400">
        <XIcon className="w-4 h-4" /> —
      </span>
    );
  }
  // string sentinel "validity" — render that tier's validity label
  if (v === "validity") {
    return <span className="text-sm font-medium text-slate-800">{t(DESIGN_TIERS[tierIdx].validityKey)}</span>;
  }
  return <span className="text-sm font-medium text-slate-800">{v}</span>;
};

function DesignTierCard({ tier, idx, t }) {
  const featuresForTier = FEATURE_ROWS.map((row) => ({
    label: t(row.labelKey),
    rawValue: row.values[idx],
  }));
  return (
    <div
      data-testid={`design-tier-${tier.id}`}
      className={`relative rounded-2xl border-2 p-5 bg-white transition ${
        tier.highlight
          ? "border-emerald-500 shadow-lg shadow-emerald-100"
          : "border-slate-200 nc-soft-shadow"
      }`}
    >
      {tier.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
          <Star className="w-3 h-3 fill-current" /> {t("pricingPage.mostPopular")}
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t(tier.nameKey)}</h3>
        <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${tier.highlight ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
          {t(tier.focusKey)}
        </span>
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{fmtINR(tier.price)}</span>
        <span className="text-sm text-slate-400 line-through">{fmtINR(tier.mrp)}</span>
      </div>
      <div className="mt-1 text-xs font-semibold text-rose-600">{t("pricingPage.save", { pct: tier.discount })}</div>

      <ul className="mt-5 space-y-2 text-sm">
        {featuresForTier.map((f) => {
          const isBool = typeof f.rawValue === "boolean";
          const isOn = isBool ? f.rawValue : true;
          const validityLabel = f.rawValue === "validity" ? t(tier.validityKey) : null;
          return (
            <li key={f.label} className="flex items-start gap-2">
              {isOn
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                : <XIcon className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />}
              <span className={isBool && !f.rawValue ? "text-slate-400 line-through" : "text-slate-700"}>
                {f.label}{validityLabel ? `: ${validityLabel}` : ""}
              </span>
            </li>
          );
        })}
      </ul>

      <Link
        to={`/design-checkout/${tier.id}`}
        className={`mt-6 w-full justify-center ${tier.highlight ? "nc-btn-primary" : "nc-btn-secondary"}`}
        data-testid={`design-tier-cta-${tier.id}`}
      >
        {t("pricingPage.getStarted")}
      </Link>
    </div>
  );
}

function DesignSection({ t }) {
  return (
    <div data-testid="design-pricing-section">
      <div className="text-center max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
          {t("pricingPage.sectionTitle")}
        </h2>
        <div className="mt-3 flex justify-center">
          <span
            data-testid="design-special-offer"
            className="inline-block rounded-full bg-yellow-200/80 ring-1 ring-yellow-400/70 text-amber-900 font-semibold text-sm md:text-base px-4 py-1.5"
          >
            {t("pricingPage.specialOffer")}
          </span>
        </div>
        <p className="mt-3 text-slate-600">
          {t("pricingPage.sectionSub")}{" "}
          <span className="font-semibold text-rose-600">{t("pricingPage.customisedForPlot")}</span>
        </p>
        <p className="mt-3 text-sm text-rose-600 max-w-2xl mx-auto" data-testid="design-payment-note">
          {t("pricingPage.paymentNote")}
        </p>
      </div>

      {/* Mobile / tablet — cards */}
      <div className="mt-8 grid sm:grid-cols-2 lg:hidden gap-5">
        {DESIGN_TIERS.map((tier, i) => <DesignTierCard key={tier.id} tier={tier} idx={i} t={t} />)}
      </div>

      {/* Desktop — comparison table */}
      <div className="mt-8 hidden lg:block">
        <div className="grid grid-cols-5 gap-4 items-end">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider pb-3">
            {t("pricingPage.compare")}
          </div>
          {DESIGN_TIERS.map((tier) => (
            <div
              key={tier.id}
              data-testid={`design-col-head-${tier.id}`}
              className={`relative rounded-t-2xl border-2 border-b-0 p-5 text-center ${
                tier.highlight ? "border-emerald-500 bg-gradient-to-b from-emerald-50 to-white" : "border-slate-200 bg-white"
              }`}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full">
                  <Star className="w-3 h-3 fill-current" /> {t("pricingPage.popular")}
                </span>
              )}
              <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t(tier.nameKey)}</h3>
              <div className={`mt-1 text-[11px] font-semibold uppercase tracking-wider ${tier.highlight ? "text-emerald-700" : "text-slate-500"}`}>
                {t(tier.focusKey)}
              </div>
              <div className="mt-4">
                <div className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{fmtINR(tier.price)}</div>
                <div className="mt-1 text-xs text-slate-400 line-through">{fmtINR(tier.mrp)}</div>
                <div className="mt-1 inline-block text-[11px] font-semibold bg-rose-50 text-rose-700 px-2 py-0.5 rounded">
                  {t("pricingPage.off", { pct: tier.discount })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-2 border-t-0 border-slate-200 rounded-b-2xl overflow-hidden bg-white">
          <table className="w-full">
            <tbody>
              {FEATURE_ROWS.map((row, ri) => (
                <tr key={row.id} className={ri % 2 === 0 ? "bg-slate-50/40" : "bg-white"}>
                  <td className="w-[20%] p-4 text-sm font-medium text-slate-700 border-r border-slate-100">
                    {t(row.labelKey)}
                  </td>
                  {row.values.map((v, ci) => (
                    <td
                      key={ci}
                      className={`w-[20%] p-4 text-center ${DESIGN_TIERS[ci].highlight ? "bg-emerald-50/30" : ""}`}
                      data-testid={`design-cell-${row.id}-${DESIGN_TIERS[ci].id}`}
                    >
                      <ValueCell v={v} highlight={DESIGN_TIERS[ci].highlight} t={t} tierIdx={ci} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-slate-200">
                <td className="p-4" />
                {DESIGN_TIERS.map((tier) => (
                  <td key={tier.id} className={`p-4 text-center ${tier.highlight ? "bg-emerald-50/30" : ""}`}>
                    <Link
                      to={`/design-checkout/${tier.id}`}
                      className={`inline-flex w-full justify-center ${tier.highlight ? "nc-btn-primary" : "nc-btn-secondary"}`}
                      data-testid={`design-tier-cta-desktop-${tier.id}`}
                    >
                      {t("pricingPage.getStarted")}
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-slate-500 max-w-2xl mx-auto">
        {t("pricingPage.footnote")}
      </p>

      <div className="mt-10 rounded-3xl bg-slate-900 text-white p-8 md:p-10 text-center">
        <h3 className="text-2xl font-semibold" style={{ fontFamily: "Poppins" }}>{t("pricingPage.notSure")}</h3>
        <p className="mt-2 text-slate-300 text-sm max-w-xl mx-auto">
          {t("pricingPage.notSureSub")}
        </p>
        <div className="mt-5 flex flex-wrap gap-3 justify-center">
          <Link to="/customize" className="nc-btn-primary"><Phone className="w-4 h-4" /> {t("pricingPage.talkAdvisor")}</Link>
          <Link to="/plans" className="nc-btn-secondary"><ShoppingCart className="w-4 h-4" /> {t("pricingPage.browseReady")}</Link>
        </div>
      </div>
    </div>
  );
}

export default function Pricing() {
  const { t } = useI18n();
  const loc = useLocation();
  const [params, setParams] = useSearchParams();

  // Default to construction when arriving via /cost-calculator or ?tab=construction
  const initialTab = useMemo(() => {
    const q = params.get("tab");
    if (q === "construction" || q === "design") return q;
    if (loc.pathname.startsWith("/cost-calculator")) return "construction";
    return "design";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (tab === "design") next.delete("tab");
    else next.set("tab", tab);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onCostCalculatorRoute = loc.pathname.startsWith("/cost-calculator");

  return (
    <div data-testid="pricing-page">
      <SEO
        title={onCostCalculatorRoute ? "Construction Cost Calculator" : "Pricing — Design Packages"}
        description={onCostCalculatorRoute
          ? "Estimate your construction cost per Sqft based on quality tier, city and built-up area."
          : "Compare NakshaCart design tiers — Naksha, Architectural, Construction and Structural packages with transparent pricing."}
        path={onCostCalculatorRoute ? "/cost-calculator" : "/pricing"}
      />
      {/* Page header */}
      <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
        <div className="nc-container py-12 md:py-14 text-center">
          <span className="nc-chip"><Sparkles className="w-3.5 h-3.5" /> {t("nav.pricing") || "Pricing"}</span>
          <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Poppins" }}>
            {t("pricingPage.title")}
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            {t("pricingPage.sub")}
          </p>

          {/* Tab switcher — dropdown on mobile, pills on md+ */}
          <div className="mt-7 flex justify-center">
            <PricingTabSelect tab={tab} setTab={setTab} t={t} />
          </div>
        </div>
      </div>

      <div className="nc-container py-10">
        {tab === "design" ? (
          <DesignSection t={t} />
        ) : (
          <div data-testid="construction-pricing-section">
            <CostCalculator />
          </div>
        )}
      </div>
    </div>
  );
}

function PricingTabSelect({ tab, setTab, t }) {
  const TABS = [
    { id: "design", label: t("pricingPage.tabDesign"), icon: PencilRuler, sub: t("pricingPage.tabDesignSub") },
    { id: "construction", label: t("pricingPage.tabConstruction"), icon: Calculator, sub: t("pricingPage.tabConstructionSub") },
  ];
  return (
    <>
      {/* Mobile — native select dropdown */}
      <div className="md:hidden w-full max-w-xs">
        <div className="relative">
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            className="appearance-none w-full bg-white border-2 border-slate-200 rounded-xl px-4 py-3 pr-10 text-sm font-semibold text-slate-900 focus:outline-none focus:border-emerald-500"
            data-testid="pricing-tab-select-mobile"
          >
            {TABS.map((o) => (
              <option key={o.id} value={o.id}>{o.label} — {o.sub}</option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        </div>
      </div>

      {/* Desktop — segmented pill control */}
      <div className="hidden md:inline-flex bg-white border-2 border-slate-200 rounded-2xl p-1.5 nc-soft-shadow">
        {TABS.map((o) => {
          const active = tab === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setTab(o.id)}
              data-testid={`pricing-tab-${o.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                active
                  ? "bg-gradient-to-r from-emerald-500 to-sky-500 text-white shadow-md"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <o.icon className="w-4 h-4" /> {o.label}
            </button>
          );
        })}
      </div>
    </>
  );
}
